import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import { BarChart3, TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import { usePharmacies } from '../context/PharmacyContext';
import SimpleDateRangePicker from '../components/SimpleDateRangePicker';
import ModernDateRangePicker from '../components/ModernDateRangePicker';
import SuperAdminPharmacyFilter from '../components/SuperAdminPharmacyFilter';
import ExpensePharmacyFilter from '../components/ExpensePharmacyFilter';
import ExpenseDateRangePicker from '../components/ExpenseDateRangePicker';
import useGranularPermissions from '../hooks/useGranularPermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function Analytics() {
  const { getPharmacyIdsParam, selectedPharmacyIds } = usePharmacies();
  const {
    canViewTotals,
    canFilterPeriod
  } = useGranularPermissions();
  const [trends, setTrends] = useState([]);
  const [revenueCategories, setRevenueCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState('line');
  const [currentUser] = useState(() => JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user') || '{}'));
  const [superAdminPharmacyFilter, setSuperAdminPharmacyFilter] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });
  
  // États pour l'analyse détaillée des dépenses
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [revenuesData, setRevenuesData] = useState([]);
  const [expensesAnalysis, setExpensesAnalysis] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, selectedPharmacyIds, superAdminPharmacyFilter]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      if (!dateRange?.from) return;

      const start = format(dateRange.from, "yyyy-MM-dd'T'00:00:00");
      const end = format(dateRange.to || dateRange.from, "yyyy-MM-dd'T'23:59:59");
      
      // For super admin, use the pharmacy filter directly
      let pharmacyIds;
      if (currentUser?.role === 'super_admin') {
        // Super admin: use the filter value directly
        pharmacyIds = superAdminPharmacyFilter === 'all' ? 'all' : superAdminPharmacyFilter;
      } else {
        // Regular admin: use pharmacy context
        pharmacyIds = getPharmacyIdsParam();
      }

      const headers = { 'X-User-Id': currentUser?.id || '' };

      // Déterminer si on doit filtrer par created_by
      // Admin et Super Admin voient tout, les autres voient uniquement leurs données
      const shouldFilterByUser = currentUser?.role !== 'admin' && currentUser?.role !== 'super_admin';
      const createdByFilter = shouldFilterByUser ? currentUser?.id : undefined;

      // Préparer les params pour les requêtes
      const baseParams = { 
        start_date: start, 
        end_date: end, 
        pharmacy_ids: pharmacyIds 
      };
      
      if (createdByFilter) {
        baseParams.created_by = createdByFilter;
      }

      const expensesParams = { ...baseParams };
      const revenuesParams = { ...baseParams };
      
      const results = await Promise.allSettled([
        axios.get(`${API}/analytics/summary`, { params: baseParams, headers }),
        axios.get(`${API}/analytics/trends`, { params: { ...baseParams, period: 'day' }, headers }),
        axios.get(`${API}/analytics/by-category`, { params: { ...baseParams, type: 'revenue' }, headers }),
        axios.get(`${API}/analytics/by-category`, { params: { ...baseParams, type: 'expense' }, headers }),
        axios.get(`${API}/expenses`, { headers, params: expensesParams }),
        axios.get(`${API}/categories`, { headers, params: { type: 'expense', pharmacy_ids: pharmacyIds } }),
        axios.get(`${API}/revenues`, { headers, params: revenuesParams }),
        axios.get(`${API}/expenses/analysis`, { headers, params: baseParams })
      ]);

      setSummary(results[0].status === 'fulfilled' ? results[0].value.data : null);
      setTrends(results[1].status === 'fulfilled' ? results[1].value.data : []);
      setRevenueCategories(results[2].status === 'fulfilled' ? results[2].value.data : []);
      setExpenseCategories(results[3].status === 'fulfilled' ? results[3].value.data : []);
      setExpenses(results[4].status === 'fulfilled' ? results[4].value.data : []);
      setCategories(results[5].status === 'fulfilled' ? results[5].value.data : []);
      setRevenuesData(results[6].status === 'fulfilled' ? results[6].value.data : []);
      setExpensesAnalysis(results[7].status === 'fulfilled' ? results[7].value.data : []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-DZ', { 
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value) + ' DA';
  };

  const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#ef4444', '#f97316', '#14b8a6', '#6366f1', '#84cc16'];

  // Fonctions pour l'analyse détaillée des dépenses
  const calculateCA = () => {
    return revenuesData.reduce((sum, rev) => sum + (rev.amount || 0), 0);
  };

  const calculateTrendData = () => {
    const monthlyAchats = {};
    const monthlyDepenses = {};
    const monthlyCA = {};
    
    expenses.forEach(exp => {
      const month = format(parseSafeDate(exp.date), 'MMM yyyy', { locale: fr });
      const category = categories.find(c => c.id === exp.category_id);
      
      if (category) {
        if (category.parent_category === 'Achat') {
          monthlyAchats[month] = (monthlyAchats[month] || 0) + exp.amount;
        } else if (category.parent_category === 'Charge' || category.parent_category === 'Salaire') {
          monthlyDepenses[month] = (monthlyDepenses[month] || 0) + exp.amount;
        }
      }
    });
    
    revenuesData.forEach(rev => {
      const month = format(parseSafeDate(rev.date), 'MMM yyyy', { locale: fr });
      monthlyCA[month] = (monthlyCA[month] || 0) + rev.amount;
    });
    
    const chartData = [];
    const diffDays = Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24));
    const monthsToShow = Math.min(Math.ceil(diffDays / 30), 12);
    
    for (let i = monthsToShow - 1; i >= 0; i--) {
      const date = subMonths(dateRange.to, i);
      const monthKey = format(date, 'MMM yyyy', { locale: fr });
      chartData.push({
        month: format(date, 'MMM', { locale: fr }),
        achats: monthlyAchats[monthKey] || 0,
        depenses: monthlyDepenses[monthKey] || 0,
        ca: monthlyCA[monthKey] || 0
      });
    }
    
    return chartData;
  };

  const calculateTopSuppliers = () => {
    const supplierTotals = {};
    
    expenses.forEach(exp => {
      const supplier = exp.supplier || 'Non spécifié';
      supplierTotals[supplier] = (supplierTotals[supplier] || 0) + exp.amount;
    });
    
    const sorted = Object.entries(supplierTotals)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
    
    return sorted;
  };

  const calculateExpenseBreakdown = () => {
    let achats = 0;
    let charges = 0;
    let salaires = 0;
    
    expenses.forEach(exp => {
      const category = categories.find(c => c.id === exp.category_id);
      if (category) {
        if (category.parent_category === 'Achat') {
          achats += exp.amount;
        } else if (category.parent_category === 'Charge') {
          charges += exp.amount;
        } else if (category.parent_category === 'Salaire') {
          salaires += exp.amount;
        }
      }
    });
    
    return [
      { name: 'Achats', value: achats, color: '#3b82f6' },
      { name: 'Charges', value: charges, color: '#f59e0b' },
      { name: 'Salaires', value: salaires, color: '#ef4444' }
    ].filter(item => item.value > 0);
  };

  const trendData = calculateTrendData();
  const topSuppliers = calculateTopSuppliers();
  const expenseBreakdown = calculateExpenseBreakdown();
  const totalCA = calculateCA();
  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const expenseToCARatio = totalCA > 0 ? ((totalExpenses / totalCA) * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="analytics-page">
      {/* En-tête */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Analyses Financières Détaillées</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">{summary?.period}</p>
        </div>
      </div>

      {/* Filtres Pharmacie + Dates - Modèle unifié */}
      <div className="bg-white rounded-3xl shadow-md p-6">
        <div className="grid grid-cols-2 gap-6">
          {/* Filtre Pharmacie */}
          {currentUser?.role === 'super_admin' && (
            <div>
              <ExpensePharmacyFilter
                selectedPharmacies={superAdminPharmacyFilter}
                onChange={setSuperAdminPharmacyFilter}
                currentUser={currentUser}
              />
            </div>
          )}
          
          {/* Filtre Date */}
          {canFilterPeriod('analytics') && (
            <div>
              <ExpenseDateRangePicker
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            </div>
          )}
        </div>
      </div>

      {/* Chart Type Selector */}
      <div className="flex justify-end">
        <Select value={chartType} onValueChange={setChartType}>
          <SelectTrigger className="w-40" data-testid="chart-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="line">Lignes</SelectItem>
            <SelectItem value="bar">Barres</SelectItem>
            <SelectItem value="area">Aires</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      {canViewTotals('analytics') ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="stat-card border-l-4 border-l-green-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Revenus</CardTitle>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{formatCurrency(summary?.total_revenues || 0)}</div>
              <p className="text-xs text-gray-500 mt-2">{summary?.revenues_count} transactions</p>
            </CardContent>
          </Card>

          <Card className="stat-card border-l-4 border-l-red-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Dépenses</CardTitle>
              <TrendingDown className="w-5 h-5 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{formatCurrency(summary?.total_expenses || 0)}</div>
              <p className="text-xs text-gray-500 mt-2">{summary?.expenses_count} transactions</p>
            </CardContent>
          </Card>

          <Card className="stat-card border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Résultat Net</CardTitle>
              <BarChart3 className="w-5 h-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${summary?.balance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                {formatCurrency(summary?.balance || 0)}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {summary?.balance >= 0 ? 'Bénéfice' : 'Déficit'}
              </p>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <BarChart3 className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
          <p className="text-sm text-yellow-700">Vous n'avez pas la permission de voir les analyses financières</p>
        </div>
      )}

      {/* Main Trends Chart */}
      <Card data-testid="trends-chart">
        <CardHeader>
          <CardTitle>Évolution Temporelle des Finances</CardTitle>
          <CardDescription>Comparaison des revenus et dépenses dans le temps</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            {chartType === 'line' ? (
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line type="monotone" dataKey="revenues" stroke="#10b981" strokeWidth={3} name="Revenus" />
                <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={3} name="Dépenses" />
                <Line type="monotone" dataKey="balance" stroke="#0ea5e9" strokeWidth={3} name="Solde" strokeDasharray="5 5" />
              </LineChart>
            ) : chartType === 'bar' ? (
              <BarChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="revenues" fill="#10b981" name="Revenus" />
                <Bar dataKey="expenses" fill="#ef4444" name="Dépenses" />
              </BarChart>
            ) : (
              <AreaChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Area type="monotone" dataKey="revenues" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Revenus" />
                <Area type="monotone" dataKey="expenses" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} name="Dépenses" />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Category Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="revenue-by-category">
          <CardHeader>
            <CardTitle>Analyse des Revenus par Catégorie</CardTitle>
            <CardDescription>Répartition détaillée</CardDescription>
          </CardHeader>
          <CardContent>
            {revenueCategories.length > 0 ? (
              <div className="space-y-4">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={revenueCategories}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {revenueCategories.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {revenueCategories.map((cat, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center">
                        <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                        <span className="text-sm font-medium">{cat.name}</span>
                      </div>
                      <span className="text-sm font-bold text-green-600">{formatCurrency(cat.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">Aucune donnée disponible</div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="expense-by-category">
          <CardHeader>
            <CardTitle>Analyse des Dépenses par Catégorie</CardTitle>
            <CardDescription>Répartition détaillée</CardDescription>
          </CardHeader>
          <CardContent>
            {expenseCategories.length > 0 ? (
              <div className="space-y-4">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={expenseCategories} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {expenseCategories.map((cat, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm font-medium">{cat.name}</span>
                      <span className="text-sm font-bold text-red-600">{formatCurrency(cat.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">Aucune donnée disponible</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Performance Indicators */}
      <Card>
        <CardHeader>
          <CardTitle>Indicateurs de Performance</CardTitle>
          <CardDescription>Métriques clés pour la période sélectionnée</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <p className="text-sm text-gray-600 mb-1">Revenu Moyen/Transaction</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(summary?.revenues_count > 0 ? summary.total_revenues / summary.revenues_count : 0)}
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-red-50 to-rose-50 rounded-lg border border-red-200">
              <p className="text-sm text-gray-600 mb-1">Dépense Moyenne/Transaction</p>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(summary?.expenses_count > 0 ? summary.total_expenses / summary.expenses_count : 0)}
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-600 mb-1">Taux de Marge</p>
              <p className="text-2xl font-bold text-blue-600">
                {summary?.total_revenues > 0 ? ((summary.balance / summary.total_revenues) * 100).toFixed(1) : 0}%
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg border border-purple-200">
              <p className="text-sm text-gray-600 mb-1">Ratio Dépenses/Revenus</p>
              <p className="text-2xl font-bold text-purple-600">
                {summary?.total_revenues > 0 ? ((summary.total_expenses / summary.total_revenues) * 100).toFixed(1) : 0}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SECTION ANALYSE DÉTAILLÉE DES DÉPENSES */}
      {expensesAnalysis && (
        <div className="space-y-6 mt-12">
          <div className="border-t-2 border-gray-200 pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">💼 Analyse Détaillée des Dépenses</h2>
          </div>

          {/* KPIs Dépenses avec % du CA */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-0 shadow-md rounded-2xl bg-gradient-to-br from-gray-50 to-white border-l-4 border-l-gray-400">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Dépenses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{formatCurrency(expensesAnalysis.total_depenses || 0)}</div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-300">
                    {totalCA > 0 ? ((expensesAnalysis.total_depenses / totalCA) * 100).toFixed(1) : 0}% du CA
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md rounded-2xl bg-gradient-to-br from-blue-50 to-white border-l-4 border-l-blue-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-blue-600">🛒 Achats</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-700">{formatCurrency(expensesAnalysis.achats || 0)}</div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                    {totalCA > 0 ? ((expensesAnalysis.achats / totalCA) * 100).toFixed(1) : 0}% du CA
                  </Badge>
                  <span className="text-xs text-gray-500">• {expensesAnalysis.pourcentage_achats}% du total</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md rounded-2xl bg-gradient-to-br from-amber-50 to-white border-l-4 border-l-amber-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-amber-600">💼 Charges + Salaires</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-amber-700">{formatCurrency(expensesAnalysis.depenses || 0)}</div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                    {totalCA > 0 ? ((expensesAnalysis.depenses / totalCA) * 100).toFixed(1) : 0}% du CA
                  </Badge>
                  <span className="text-xs text-gray-500">• {expensesAnalysis.pourcentage_depenses}% du total</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Graphiques d'analyse */}
          <div className="space-y-6">
            {/* Graphique 1 : Évolution Temporelle */}
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardHeader className="border-b border-gray-100">
                <CardTitle className="text-xl font-bold text-gray-800">📈 Évolution : Achats, Dépenses & CA</CardTitle>
                <CardDescription className="text-sm text-gray-500">
                  Comparaison temporelle • {format(dateRange.from, 'dd MMM', { locale: fr })} - {format(dateRange.to, 'dd MMM yyyy', { locale: fr })}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: '12px' }} />
                    <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '20px' }} />
                    <Line type="monotone" dataKey="ca" stroke="#10b981" strokeWidth={3} name="CA" dot={{ fill: '#10b981', r: 5 }} />
                    <Line type="monotone" dataKey="achats" stroke="#3b82f6" strokeWidth={3} name="Achats" dot={{ fill: '#3b82f6', r: 5 }} />
                    <Line type="monotone" dataKey="depenses" stroke="#f59e0b" strokeWidth={3} name="Charges + Salaires" dot={{ fill: '#f59e0b', r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Row 2 : Top Fournisseurs + Répartition */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-0 shadow-lg rounded-2xl">
                <CardHeader className="border-b border-gray-100">
                  <CardTitle className="text-xl font-bold text-gray-800">👥 Top 10 Fournisseurs</CardTitle>
                  <CardDescription>Par montant total dépensé</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={topSuppliers} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" stroke="#6b7280" style={{ fontSize: '11px' }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" width={120} stroke="#6b7280" style={{ fontSize: '11px' }} />
                      <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ borderRadius: '12px' }} />
                      <Bar dataKey="total" fill="#8b5cf6" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg rounded-2xl">
                <CardHeader className="border-b border-gray-100">
                  <CardTitle className="text-xl font-bold text-gray-800">🎯 Répartition des Dépenses</CardTitle>
                  <CardDescription>Par type de dépense</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={expenseBreakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                        outerRadius={120}
                        dataKey="value"
                      >
                        {expenseBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Graphique 4 : Ratio Dépenses/CA */}
            <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-br from-blue-50 to-white">
              <CardHeader className="border-b border-gray-100">
                <CardTitle className="text-xl font-bold text-gray-800">⚡ Ratio Dépenses / CA</CardTitle>
                <CardDescription>Indicateur de rentabilité • Total CA : {formatCurrency(totalCA)}</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Taux de dépenses</span>
                      <span className={`text-2xl font-bold ${
                        expenseToCARatio > 70 ? 'text-red-600' : 
                        expenseToCARatio > 50 ? 'text-orange-600' : 
                        'text-green-600'
                      }`}>
                        {expenseToCARatio}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          expenseToCARatio > 70 ? 'bg-gradient-to-r from-red-500 to-red-600' : 
                          expenseToCARatio > 50 ? 'bg-gradient-to-r from-orange-500 to-orange-600' : 
                          'bg-gradient-to-r from-green-500 to-green-600'
                        }`}
                        style={{ width: `${Math.min(expenseToCARatio, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 pt-4">
                    <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
                      <div className="text-sm text-gray-600 mb-1">Total Dépenses</div>
                      <div className="text-lg font-bold text-gray-900">{formatCurrency(totalExpenses)}</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
                      <div className="text-sm text-gray-600 mb-1">CA</div>
                      <div className="text-lg font-bold text-green-600">{formatCurrency(totalCA)}</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
                      <div className="text-sm text-gray-600 mb-1">Marge Brute</div>
                      <div className="text-lg font-bold text-blue-600">{formatCurrency(totalCA - totalExpenses)}</div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-xl border-2 ${
                    expenseToCARatio > 70 ? 'bg-red-50 border-red-300' : 
                    expenseToCARatio > 50 ? 'bg-orange-50 border-orange-300' : 
                    'bg-green-50 border-green-300'
                  }`}>
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">
                        {expenseToCARatio > 70 ? '⚠️' : expenseToCARatio > 50 ? '⚡' : '✅'}
                      </span>
                      <div>
                        <div className="font-semibold text-gray-900">
                          {expenseToCARatio > 70 ? 'Attention : Taux élevé' : 
                           expenseToCARatio > 50 ? 'Modéré : Surveiller' : 
                           'Excellent : Sous contrôle'}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {expenseToCARatio > 70 ? 'Dépenses > 70% du CA. Optimisation recommandée.' : 
                           expenseToCARatio > 50 ? 'Dépenses dans la norme, optimisables.' : 
                           'Dépenses bien maîtrisées !'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

export default Analytics;
